import React, { useState } from 'react';
import { Home, Package, Settings, Trash2 } from 'lucide-react';

interface WidgetShellProps {
  isEditMode: boolean;
  onConfigOpen: () => void;
  onRemove: () => void;
  isDark: boolean;
  scopeBadge?: {
    label: string;
    type: 'warehouse' | 'storage_area';
    isCustom: boolean;
  };
  showBadgeInViewMode?: boolean;
  children: React.ReactNode;
}

const WidgetShell: React.FC<WidgetShellProps> = ({
  isEditMode,
  onConfigOpen,
  onRemove,
  isDark,
  scopeBadge,
  showBadgeInViewMode = false,
  children,
}) => {
  const [hovered, setHovered] = useState(false);
  const showScopeBadge = !!scopeBadge && (isEditMode || showBadgeInViewMode || scopeBadge.isCustom);

  return (
    <div
      className="relative h-full w-full flex flex-col items-stretch justify-start min-h-0"
      onMouseEnter={() => isEditMode && setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {children}

      {showScopeBadge && scopeBadge && (
        <div
          className={`absolute left-2 top-2 z-10 max-w-[72%] rounded px-1.5 py-0.5 text-[10px] leading-4 flex items-center gap-1 ${
            isDark ? 'bg-[#171a1f]/70 text-gray-400' : 'bg-white/80 text-gray-500'
          }`}
          title={scopeBadge.label}
        >
          {scopeBadge.type === 'warehouse' ? <Home size={10} /> : <Package size={10} />}
          <span className="truncate">{scopeBadge.label}</span>
          {scopeBadge.isCustom && (
            <span
              className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isDark ? 'bg-blue-400/70' : 'bg-blue-500/70'}`}
              title="Custom scope"
            />
          )}
        </div>
      )}

      {isEditMode && (
        <>
          {/* Dashed edit-mode border overlay */}
          <div
            className="absolute inset-0 rounded pointer-events-none transition-opacity"
            style={{
              border: `2px dashed ${hovered ? 'rgba(99,149,255,0.6)' : 'rgba(99,149,255,0.2)'}`,
            }}
          />

          {/* Hover controls */}
          {hovered && (
            <div className="absolute top-2 right-2 flex items-center gap-1 z-10">
              <button
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => { e.stopPropagation(); onConfigOpen(); }}
                className={`w-6 h-6 rounded flex items-center justify-center shadow transition-colors ${
                  isDark
                    ? 'bg-[#2b3140] hover:bg-[#404963] text-gray-300'
                    : 'bg-white hover:bg-gray-50 text-gray-600 border border-gray-200'
                }`}
                title="Widget settings"
              >
                <Settings size={11} />
              </button>
              <button
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => { e.stopPropagation(); onRemove(); }}
                className="w-6 h-6 rounded flex items-center justify-center shadow transition-colors bg-red-500/20 hover:bg-red-500/40 text-red-400"
                title="Remove widget"
              >
                <Trash2 size={11} />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default WidgetShell;
